import { useState, useEffect } from 'react';
import axios from 'axios';
import AdminNav from '../components/admin/AdminNav';
import fixEncoding from '../utils/fixEncoding';
import '../styles/pages/AdminCategoryMappingsPage.scss';

const API_URL = process.env.REACT_APP_API_URL;

const PROVIDER_OPTIONS = [
  { value: 'cdo',        label: 'CDO' },
  { value: 'marpico',    label: 'Marpico' },
  { value: 'shopify',    label: 'Boton Promo' },
  { value: 'promoopcion',label: 'PromoOpcion' },
  { value: 'catalogos',  label: 'Catalogos Promocionales' },
];

const AdminCategoryMappingsPage = () => {
  const [provider,        setProvider]        = useState('cdo');
  const [mappings,        setMappings]        = useState([]);
  const [taxonomies,      setTaxonomies]      = useState([]);
  const [loading,         setLoading]         = useState(true);
  const [error,           setError]           = useState('');
  const [success,         setSuccess]         = useState('');

  // ── Filtros ───────────────────────────────────────────────
  const [filterText,      setFilterText]      = useState('');
  const [filterCatId,     setFilterCatId]     = useState('');

  // ── Edición ───────────────────────────────────────────────
  const [editingId,               setEditingId]               = useState(null);
  const [editTargetCategoryId,    setEditTargetCategoryId]    = useState('');
  const [editTargetSubcategoryId, setEditTargetSubcategoryId] = useState('');
  const [editProviderSubKey,      setEditProviderSubKey]      = useState(''); // bodega sin taxonomía: texto libre; con taxonomía: nombre de subcategoría
  const [editBodegaCatId,         setEditBodegaCatId]         = useState(''); // id/"ghost-N" de la categoría de bodega elegida
  const [editProviderCatKey,      setEditProviderCatKey]      = useState('');

  // ── Alta ──────────────────────────────────────────────────
  const [showAddForm,           setShowAddForm]           = useState(false);
  const [newProviderCatKey,     setNewProviderCatKey]     = useState('');
  const [newProviderSubKey,     setNewProviderSubKey]     = useState('');
  const [newTargetCategoryId,   setNewTargetCategoryId]   = useState('');
  const [newTargetSubcategoryId,setNewTargetSubcategoryId]= useState('');

  // ── Taxonomía de la bodega (para desplegables en el alta) ──
  // Solo disponible para bodegas cuya API se puede consultar en vivo (hoy: Catalogos).
  const [bodegaTax,        setBodegaTax]        = useState(null); // { categorias:[{id,nombre,sintetica,subcategorias:[...]}], flat:[] }
  const [bodegaTaxLoading, setBodegaTaxLoading] = useState(false);
  const [bodegaTaxError,   setBodegaTaxError]   = useState('');
  const [newBodegaCatId,   setNewBodegaCatId]   = useState('');   // id (o "ghost-N") de la categoría elegida

  const getAuthHeader = () => ({
    Authorization: `Bearer ${localStorage.getItem('admToken')}`,
  });

  const refetchMappings = async () => {
    const res = await axios.get(`${API_URL}/admin/category-mappings`, {
      params: { provider },
      headers: getAuthHeader(),
    });
    setMappings(res.data);
  };

  useEffect(() => {
    const fetchTaxonomies = async () => {
      try {
        const res = await axios.get(`${API_URL}/admin/products/taxonomies`, { headers: getAuthHeader() });
        setTaxonomies(res.data);
      } catch {
        setError('Error al cargar categorías del sitio.');
      }
    };
    fetchTaxonomies();
  }, []);

  // Carga la taxonomía de la bodega (categorías/subcategorías reales de su API).
  useEffect(() => {
    let cancelled = false;
    const fetchBodegaTax = async () => {
      setBodegaTax(null);
      setBodegaTaxError('');
      setNewBodegaCatId('');
      if (!provider) return;
      setBodegaTaxLoading(true);
      try {
        const res = await axios.get(`${API_URL}/admin/category-mappings/provider-taxonomy`, {
          params: { provider },
          headers: getAuthHeader(),
        });
        if (cancelled) return;
        if (res.data?.supported && Array.isArray(res.data.categorias) && res.data.categorias.length) {
          setBodegaTax({ categorias: res.data.categorias, flat: res.data.flat || [] });
        } else {
          setBodegaTax(null); // bodega sin taxonomía en vivo → se usa texto libre
        }
      } catch (err) {
        if (cancelled) return;
        setBodegaTax(null);
        setBodegaTaxError(
          err.response?.data?.message || 'No se pudo cargar el listado de categorías de la bodega. Puedes escribirla a mano.'
        );
      } finally {
        if (!cancelled) setBodegaTaxLoading(false);
      }
    };
    fetchBodegaTax();
    return () => { cancelled = true; };
  }, [provider]);

  useEffect(() => {
    const fetchMappings = async () => {
      if (!provider) return;
      setLoading(true);
      setError('');
      try {
        const res = await axios.get(`${API_URL}/admin/category-mappings`, {
          params: { provider },
          headers: getAuthHeader(),
        });
        setMappings(res.data);
      } catch (err) {
        setError(err.response?.data?.message || 'Error al cargar mapeos.');
      } finally {
        setLoading(false);
      }
    };
    fetchMappings();
  }, [provider]);

  // ── Filtrado client-side ──────────────────────────────────
  const filteredMappings = mappings.filter((m) => {
    if (filterCatId && m.targetCategoryId !== parseInt(filterCatId, 10)) return false;
    if (filterText) {
      const q = filterText.toLowerCase();
      const inBodega   = fixEncoding(m.providerCategoryKey || '').toLowerCase().includes(q);
      const inSamples  = m.subcategoryBodegaSamples?.some(s => fixEncoding(s).toLowerCase().includes(q));
      const inCat      = fixEncoding(m.targetCategory?.name || '').toLowerCase().includes(q);
      const inSub      = fixEncoding(m.targetSubcategory?.name || '').toLowerCase().includes(q);
      if (!inBodega && !inSamples && !inCat && !inSub) return false;
    }
    return true;
  });

  const subcategoriesForCategory = (categoryId) => {
    const cat = taxonomies.find((c) => c.id === parseInt(categoryId, 10));
    return cat?.subcategories || [];
  };

  // ── Taxonomía de la bodega: helpers ───────────────────────
  const bodegaCatById = (catId) =>
    bodegaTax?.categorias.find((c) => String(c.id) === String(catId)) || null;

  // Ubica un nombre de bodega dentro del árbol: ¿es una categoría raíz o una subcategoría?
  const bodegaNodeFor = (name) => {
    if (!bodegaTax || !name) return { catNode: null, subNode: null };
    const norm = fixEncoding(name).trim().toLowerCase();
    for (const cat of bodegaTax.categorias) {
      if (!cat.sintetica && fixEncoding(cat.nombre).trim().toLowerCase() === norm) {
        return { catNode: cat, subNode: null };
      }
      const sub = cat.subcategorias.find(
        (s) => fixEncoding(s.nombre).trim().toLowerCase() === norm
      );
      if (sub) return { catNode: cat, subNode: sub };
    }
    return { catNode: null, subNode: null };
  };

  // Nombre EXACTO que la sincronización debe hacer match (el más específico elegido).
  const deepestBodegaKey = (catId, subName, fallbackText) => {
    if (!bodegaTax) return (fallbackText || '').trim();
    if (subName) return subName;
    const cat = bodegaCatById(catId);
    if (cat && !cat.sintetica) return cat.nombre;
    return '';
  };

  // ── Alta: selección ──────────────────────────────────────
  const selectedBodegaCat = bodegaCatById(newBodegaCatId);
  const bodegaSubcategorias = selectedBodegaCat?.subcategorias || [];
  const resolvedBodegaKey = deepestBodegaKey(newBodegaCatId, newProviderSubKey, newProviderCatKey);

  // ── Edición ───────────────────────────────────────────────
  const editSelectedBodegaCat = bodegaCatById(editBodegaCatId);
  const editBodegaSubcategorias = editSelectedBodegaCat?.subcategorias || [];
  const resolvedEditBodegaKey = deepestBodegaKey(editBodegaCatId, editProviderSubKey, editProviderCatKey);

  const startEdit = (mapping) => {
    setEditingId(mapping.id);
    setEditTargetCategoryId(mapping.targetCategoryId?.toString() || '');
    setEditTargetSubcategoryId(mapping.targetSubcategoryId?.toString() || '');
    if (bodegaTax) {
      const { catNode, subNode } = bodegaNodeFor(mapping.providerCategoryKey);
      setEditBodegaCatId(catNode ? String(catNode.id) : '');
      setEditProviderCatKey(catNode && !subNode ? catNode.nombre : '');
      setEditProviderSubKey(subNode ? subNode.nombre : '');
    } else {
      setEditBodegaCatId('');
      setEditProviderCatKey('');
      setEditProviderSubKey(mapping.providerSubcategoryKey || '');
    }
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditTargetCategoryId('');
    setEditTargetSubcategoryId('');
    setEditBodegaCatId('');
    setEditProviderCatKey('');
    setEditProviderSubKey('');
  };

  const saveEdit = async () => {
    if (!editingId) return;
    if (!editTargetCategoryId) { setError('Selecciona una categoría del sitio.'); return; }

    const body = {
      targetCategoryId:    parseInt(editTargetCategoryId, 10),
      targetSubcategoryId: editTargetSubcategoryId ? parseInt(editTargetSubcategoryId, 10) : null,
    };

    if (bodegaTax) {
      const key = resolvedEditBodegaKey;
      if (!key) {
        setError('Elige la categoría de la bodega (y una subcategoría si escogiste un grupo).');
        return;
      }
      body.providerCategoryKey = key;
      body.providerSubcategoryKey = null;
    } else {
      body.providerSubcategoryKey = editProviderSubKey.trim() || null;
    }

    setError('');
    try {
      await axios.put(
        `${API_URL}/admin/category-mappings/${editingId}`,
        body,
        { headers: getAuthHeader() }
      );
      setSuccess('Mapeo actualizado.');
      setTimeout(() => setSuccess(''), 3000);
      cancelEdit();
      await refetchMappings();
    } catch (err) {
      setError(err.response?.data?.message || 'Error al guardar.');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este mapeo?')) return;
    setError('');
    try {
      await axios.delete(`${API_URL}/admin/category-mappings/${id}`, { headers: getAuthHeader() });
      setMappings((prev) => prev.filter((m) => m.id !== id));
      setSuccess('Mapeo eliminado.');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Error al eliminar.');
    }
  };

  // ── Alta ──────────────────────────────────────────────────
  const resetAddForm = () => {
    setShowAddForm(false);
    setNewProviderCatKey('');
    setNewProviderSubKey('');
    setNewBodegaCatId('');
    setNewTargetCategoryId('');
    setNewTargetSubcategoryId('');
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    const providerCategoryKey = bodegaTax ? resolvedBodegaKey : newProviderCatKey.trim();

    if (!providerCategoryKey) {
      setError(
        bodegaTax
          ? 'Elige la categoría de la bodega (y una subcategoría si escogiste un grupo).'
          : 'La categoría de la bodega es obligatoria.'
      );
      return;
    }
    if (!newTargetCategoryId) {
      setError('La categoría del sitio (En-Stock) es obligatoria.');
      return;
    }
    setError('');
    try {
      await axios.post(
        `${API_URL}/admin/category-mappings`,
        {
          provider,
          providerCategoryKey,
          providerSubcategoryKey: bodegaTax ? null : (newProviderSubKey.trim() || null),
          targetCategoryId:       parseInt(newTargetCategoryId, 10),
          targetSubcategoryId:    newTargetSubcategoryId ? parseInt(newTargetSubcategoryId, 10) : null,
        },
        { headers: getAuthHeader() }
      );
      setSuccess('Mapeo creado.');
      setTimeout(() => setSuccess(''), 3000);
      resetAddForm();
      await refetchMappings();
    } catch (err) {
      setError(err.response?.data?.message || 'Error al crear mapeo.');
    }
  };

  return (
    <div className="admin-category-mappings-page">
      <header className="admin-category-mappings-page__header">
        <h1>Mapeo de categorías</h1>
      </header>

      <AdminNav />

      <p className="admin-category-mappings-page__intro">
        Asigna las categorías que vienen de cada bodega a las categorías del sitio.
        Ejecuta una sincronización para que aparezcan aquí las categorías.
      </p>

      {error   && <div className="acm-alert acm-alert--error">{error}</div>}
      {success && <div className="acm-alert acm-alert--success">{success}</div>}

      {/* Selector de bodega */}
      <div className="admin-category-mappings-page__provider">
        <label htmlFor="provider-select">Bodega</label>
        <select id="provider-select" value={provider} onChange={(e) => setProvider(e.target.value)}>
          {PROVIDER_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>

      <div className="admin-category-mappings-page__actions">
        <button type="button" className="acm-btn acm-btn--primary" onClick={() => setShowAddForm(true)}>
          + Añadir mapeo
        </button>
      </div>

      {/* Filtros */}
      <div className="acm-filters">
        <input
          className="acm-filters__text"
          type="text"
          placeholder="Buscar por categoría o subcategoría..."
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
        />
        <select
          className="acm-filters__cat"
          value={filterCatId}
          onChange={(e) => setFilterCatId(e.target.value)}
        >
          <option value="">Todas las categorías</option>
          {taxonomies.map((c) => (
            <option key={c.id} value={c.id}>{fixEncoding(c.name)}</option>
          ))}
        </select>
        {(filterText || filterCatId) && (
          <button
            type="button"
            className="acm-btn acm-btn--ghost acm-btn--sm"
            onClick={() => { setFilterText(''); setFilterCatId(''); }}
          >
            Limpiar
          </button>
        )}
        <span className="acm-filters__count">
          {filteredMappings.length} de {mappings.length} mapeos
        </span>
      </div>

      {/* Formulario de alta */}
      {showAddForm && (
        <form className="acm-form" onSubmit={handleAdd}>
          <h3 className="acm-form__title">Nuevo mapeo</h3>

          <div className="acm-form__section-label">Bodega</div>

          {bodegaTaxLoading && (
            <p className="acm-form__hint">Cargando categorías de la bodega…</p>
          )}
          {bodegaTaxError && (
            <p className="acm-form__hint acm-form__hint--warn">{bodegaTaxError}</p>
          )}

          {bodegaTax ? (
            <>
              <div className="acm-form__row">
                <div className="acm-form__group">
                  <label>Categoría en la bodega *</label>
                  <select
                    value={newBodegaCatId}
                    onChange={(e) => {
                      setNewBodegaCatId(e.target.value);
                      setNewProviderSubKey('');
                      const cat = bodegaTax.categorias.find((c) => String(c.id) === String(e.target.value));
                      setNewProviderCatKey(cat && !cat.sintetica ? cat.nombre : '');
                    }}
                  >
                    <option value="">— Seleccionar —</option>
                    {bodegaTax.categorias.map((c) => (
                      <option key={c.id} value={c.id}>
                        {fixEncoding(c.nombre)}{c.sintetica ? ' (grupo)' : ''}
                        {c.subcategorias.length ? ` (${c.subcategorias.length})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="acm-form__group">
                  <label>
                    Subcategoría en la bodega
                    {selectedBodegaCat?.sintetica ? ' *' : ''}
                  </label>
                  <select
                    value={newProviderSubKey}
                    onChange={(e) => setNewProviderSubKey(e.target.value)}
                    disabled={!bodegaSubcategorias.length}
                  >
                    <option value="">
                      {selectedBodegaCat
                        ? (selectedBodegaCat.sintetica
                            ? '— Seleccionar —'
                            : (bodegaSubcategorias.length ? '— Toda la categoría —' : 'Sin subcategorías en la bodega'))
                        : '— Toda la categoría —'}
                    </option>
                    {bodegaSubcategorias.map((s) => (
                      <option key={s.id} value={s.nombre}>{fixEncoding(s.nombre)}</option>
                    ))}
                  </select>
                </div>
              </div>
              {resolvedBodegaKey && (
                <p className="acm-form__hint">
                  Se guardará como categoría de bodega: <strong>{fixEncoding(resolvedBodegaKey)}</strong>
                </p>
              )}
            </>
          ) : (
            <div className="acm-form__row">
              <div className="acm-form__group">
                <label>Categoría en la bodega *</label>
                <input
                  type="text"
                  value={newProviderCatKey}
                  onChange={(e) => setNewProviderCatKey(e.target.value)}
                  placeholder="Ej: ESCRITURA"
                />
              </div>
              <div className="acm-form__group">
                <label>Subcategoría en la bodega</label>
                <input
                  type="text"
                  value={newProviderSubKey}
                  onChange={(e) => setNewProviderSubKey(e.target.value)}
                  placeholder="Ej: BOLIGRAFOS (opcional)"
                />
              </div>
            </div>
          )}

          <div className="acm-form__section-label">En Stock</div>
          <div className="acm-form__row">
            <div className="acm-form__group">
              <label>Categoría En-Stock *</label>
              <select
                value={newTargetCategoryId}
                onChange={(e) => { setNewTargetCategoryId(e.target.value); setNewTargetSubcategoryId(''); }}
              >
                <option value="">— Seleccionar —</option>
                {taxonomies.map((c) => (
                  <option key={c.id} value={c.id}>{fixEncoding(c.name)}</option>
                ))}
              </select>
            </div>
            <div className="acm-form__group">
              <label>Subcategoría En-Stock</label>
              <select
                value={newTargetSubcategoryId}
                onChange={(e) => setNewTargetSubcategoryId(e.target.value)}
              >
                <option value="">— Ninguna —</option>
                {subcategoriesForCategory(newTargetCategoryId).map((s) => (
                  <option key={s.id} value={s.id}>{fixEncoding(s.name)}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="acm-form__actions">
            <button type="submit" className="acm-btn acm-btn--primary">Guardar</button>
            <button type="button" className="acm-btn acm-btn--ghost" onClick={resetAddForm}>Cancelar</button>
          </div>
        </form>
      )}

      {/* Tabla */}
      {loading ? (
        <p className="acm-loading">Cargando mapeos...</p>
      ) : (
        <div className="acm-table-wrap">
          <table className="acm-table">
            <thead>
              <tr>
                <th>Categoría en la bodega</th>
                <th>Subcategoría en la bodega</th>
                <th>Visto en el sync</th>
                <th>Categoría En-Stock</th>
                <th>Subcategoría En-Stock</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {mappings.length === 0 ? (
                <tr>
                  <td colSpan="6" className="acm-table__empty">
                    No hay mapeos para esta bodega. Añade uno o sincroniza la bodega.
                  </td>
                </tr>
              ) : filteredMappings.length === 0 ? (
                <tr>
                  <td colSpan="6" className="acm-table__empty">
                    No hay mapeos que coincidan con el filtro.
                  </td>
                </tr>
              ) : (
                filteredMappings.map((m) => {
                  const bodegaNode = bodegaTax ? bodegaNodeFor(m.providerCategoryKey) : null;
                  return (
                  <tr key={m.id}>
                    {/* Categoría en la bodega */}
                    <td>
                      {editingId === m.id && bodegaTax ? (
                        <select
                          className="acm-inline-select"
                          value={editBodegaCatId}
                          onChange={(e) => {
                            setEditBodegaCatId(e.target.value);
                            setEditProviderSubKey('');
                            const cat = bodegaCatById(e.target.value);
                            setEditProviderCatKey(cat && !cat.sintetica ? cat.nombre : '');
                          }}
                        >
                          <option value="">— Categoría —</option>
                          {bodegaTax.categorias.map((c) => (
                            <option key={c.id} value={c.id}>
                              {fixEncoding(c.nombre)}{c.sintetica ? ' (grupo)' : ''}
                              {c.subcategorias.length ? ` (${c.subcategorias.length})` : ''}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <span className="acm-badge">
                          {fixEncoding(bodegaNode?.catNode ? bodegaNode.catNode.nombre : m.providerCategoryKey)}
                        </span>
                      )}
                    </td>

                    {/* Subcategoría en la bodega */}
                    <td>
                      {editingId === m.id && bodegaTax ? (
                        <select
                          className="acm-inline-select"
                          value={editProviderSubKey}
                          onChange={(e) => setEditProviderSubKey(e.target.value)}
                          disabled={!editBodegaSubcategorias.length}
                        >
                          <option value="">
                            {editSelectedBodegaCat?.sintetica ? '— Elegir subcategoría —' : '— Toda la categoría —'}
                          </option>
                          {editBodegaSubcategorias.map((s) => (
                            <option key={s.id} value={s.nombre}>{fixEncoding(s.nombre)}</option>
                          ))}
                        </select>
                      ) : bodegaNode?.subNode ? (
                        <span className="acm-badge">{fixEncoding(bodegaNode.subNode.nombre)}</span>
                      ) : m.providerSubcategoryKey ? (
                        <span className="acm-badge">{fixEncoding(m.providerSubcategoryKey)}</span>
                      ) : (
                        <span className="acm-dash">—</span>
                      )}
                    </td>

                    {/* Visto en el sync (muestras de subcategory_bodega de productos) */}
                    <td className="acm-samples-cell">
                      {m.subcategoryBodegaSamples?.length
                        ? m.subcategoryBodegaSamples.map((s, i) => (
                            <span key={i} className="acm-sample">{fixEncoding(s)}</span>
                          ))
                        : <span className="acm-dash">—</span>
                      }
                    </td>

                    {/* Categoría En Stock */}
                    <td>
                      {editingId === m.id ? (
                        <select
                          className="acm-inline-select"
                          value={editTargetCategoryId}
                          onChange={(e) => { setEditTargetCategoryId(e.target.value); setEditTargetSubcategoryId(''); }}
                        >
                          <option value="">— Seleccionar —</option>
                          {taxonomies.map((c) => (
                            <option key={c.id} value={c.id}>{fixEncoding(c.name)}</option>
                          ))}
                        </select>
                      ) : (
                        <span className="acm-enstock-cat">
                          {m.targetCategory?.name ? fixEncoding(m.targetCategory.name) : <span className="acm-dash">—</span>}
                        </span>
                      )}
                    </td>

                    {/* Subcategoría En Stock */}
                    <td>
                      {editingId === m.id ? (
                        <select
                          className="acm-inline-select"
                          value={editTargetSubcategoryId}
                          onChange={(e) => setEditTargetSubcategoryId(e.target.value)}
                        >
                          <option value="">— Ninguna —</option>
                          {subcategoriesForCategory(editTargetCategoryId).map((s) => (
                            <option key={s.id} value={s.id}>{fixEncoding(s.name)}</option>
                          ))}
                        </select>
                      ) : (
                        <span className="acm-enstock-sub">
                          {m.targetSubcategory?.name ? fixEncoding(m.targetSubcategory.name) : <span className="acm-dash">—</span>}
                        </span>
                      )}
                    </td>

                    {/* Acciones */}
                    <td className="acm-table__actions">
                      {editingId === m.id ? (
                        <>
                          <button type="button" className="acm-btn acm-btn--primary acm-btn--sm" onClick={saveEdit}>Guardar</button>
                          <button type="button" className="acm-btn acm-btn--ghost acm-btn--sm"  onClick={cancelEdit}>Cancelar</button>
                        </>
                      ) : (
                        <>
                          <button type="button" className="acm-btn acm-btn--outline acm-btn--sm" onClick={() => startEdit(m)}>Editar</button>
                          <button type="button" className="acm-btn acm-btn--danger  acm-btn--sm" onClick={() => handleDelete(m.id)}>Eliminar</button>
                        </>
                      )}
                    </td>
                  </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminCategoryMappingsPage;
